import { type FetchBaseQueryError, createApi } from '@reduxjs/toolkit/query/react';
import { identity, pickBy } from 'lodash';
import { v4 as uuidv4 } from 'uuid';

import { API_URL } from '../../../common/constants/api-url';
import { FormMode, Marketplaces } from '../../../common/constants/enums';
import {
  IPageAsset,
  PageBannerAsset,
  PageBuyingGuide,
  PagePublisherImage,
  Product,
  Publisher,
} from '../../../common/interfaces/agg-page';
import { AuthorDto } from '../../../common/interfaces/authors-api';
import { IUpdatePageResponse } from '../../../common/interfaces/pages-api';
import { utilProductDataPreparing } from '../../../common/utils';
import { IDraft, IProductContent } from '../../../context/page-form-context';
import { IProductAiImages } from '../../../modules/pages/page-form/page-form';
import { PagesFilterState } from '../../reducers/pages/pages-filter';
import { getBaseQuery } from '../base-query';
import { brandsApiSlice } from '../brands-api/brands-api';
import { productLinksApiSlice } from '../product-links-api/product-links-api';
import { productsApiSlice } from '../products-api/products-api';
import {
  GetProductContentCopiesResBody,
  ProductAsset,
  SubmitProductContentReqBody,
  UpdatedProductAsset,
  UpdateProductReviewsReqBody,
} from '../products-api/products-api-types';
import { publishersApiSlice } from '../publishers-api/publishers-api';
import {
  AggPageV2,
  CreateAggPageReqBody,
  CreateAggPageResBody,
  DeletePageAssetsParams,
  DeleteProductAssetsParams,
  GetAggPagesV2Params,
  GetAggPagesV2ResBody,
  GetFullPageDataParams,
  GetFullPageDataResBody,
  GetPageDataParams,
  GetPageDataResBody,
  GetPageDraftsResBody,
  GetPagesResBody,
  PageFormProduct,
  ProductBannerAsset,
  ProductCardAsset,
  ProductPostAsset,
  ProductVideoAsset,
  RenewPageAssetsReqBody,
  SubmitAggPageProductsReqBody,
  SubmitAggPageProductsResBody,
  SubmitPageAssetsReqBody,
  SubmitPageProcessData,
  SubmitPagesProductsReqBody,
  SubmitProductAssetReqBody,
  UpdateAggPageV2Params,
  UpdateAggPageV2ReqBody,
  UpdatePageDataReqBody,
  UpdatePageDataResBody,
  UpdatePageStatusReqBody,
  ValidateAggPageSlugReqBody,
  ValidateAggPageSlugResBody,
  ValidateKeywordReqBody,
  ValidateKeywordResBody,
} from './pages-api-types';

export interface UpdatedAsset extends ProductAsset {
  asset_type: 'video' | 'blogs' | 'assets';
}

export const getPublisherImages = (assets: IPageAsset[] = []): PagePublisherImage[] =>
  assets
    .map((item) => ({
      assets_type: 'publisherImages' as const,
      file_data: { order: item?.order },
      url: item.img,
    }))
    .sort((a, b) => a.file_data.order - b.file_data.order);

export const getBanners = (assets: IPageAsset[] = []): PageBannerAsset[] =>
  assets.reduce<PageBannerAsset[]>((result, asset) => {
    if (asset.type === 'banner') {
      return [
        ...result,
        {
          assets_type: 'assets',
          file_data: {
            order: asset?.order,
            type: 'banner',
          },
          url: asset.img,
        },
      ];
    }
    return result;
  }, []);

export const getDrafts = (data?: GetPageDraftsResBody): IDraft[] => data?.drafts?.map((elem) => ({
  ...elem,
  in_draft: Boolean(elem.in_draft),
})) || [];

export const getContent = (product: Product, copy?: GetProductContentCopiesResBody['products'][number]) => {
  const productContent: IProductContent[] = [];
  if (copy) {
    productContent.push({
      _id: uuidv4(),
      product_id: copy.asin,
      type: 'origin',
      ...({
        ...copy.content,
        faq: copy.content?.faq || [],
        pros: copy.content?.pros?.filter((p) => p.trim().length > 0),
      }),
      tied: !product.picked_product_copy_id,
    });
    copy.product_copies.forEach((elem) => {
      if (elem.content) {
        productContent.push({
          _id: uuidv4(),
          copy_id: elem.copy_id,
          product_id: copy.asin,
          tied: product.picked_product_copy_id === elem.copy_id,
          ...{
            ...elem.content.content,
            faq: elem.content.content?.faq || [],
            pros: elem.content.content?.pros?.filter((p) => p.trim().length > 0) || [],
          },
        });
      }
    });
  }

  return { productContent };
};

export const getExistedArrayElem = <T extends unknown>(data: Array<T>) => {
  if (!Array.isArray(data)) {
    return [];
  }

  return data.reduce<number[]>((result, elem, elemIdx) => {
    if (elem && JSON.stringify(elem)
      .includes('true')) {
      result = [...result, elemIdx];
      return result;
    }

    return result;
  }, []);
};

export const pagesApiSlice = createApi({
  baseQuery: getBaseQuery({ baseUrl: API_URL }),
  endpoints: (builder) => ({
    createAggPageV2: builder.mutation<CreateAggPageResBody, CreateAggPageReqBody>({
      invalidatesTags: [{
        id: 'LIST',
        type: 'Pages',
      }],
      query: (body) => ({
        body,
        method: 'POST',
        url: '/admin/v2/agg-pages',
      }),
    }),
    downloadAggPagesV2: builder.mutation<void, GetAggPagesV2Params>({
      query: (params) => ({
        cache: 'no-cache',
        params,
        responseHandler: async (response) => {
          const blob = await response.blob();
          const contentDisposition = response.headers.get('content-disposition') ?? '';
          const match = contentDisposition.match(/filename="(.*)"/);
          const fileName = match?.[1] ?? 'export.csv';
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = fileName;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
        },
        url: '/admin/download/v2/agg-pages',
      }),
    }),
    deleteAggPageV2: builder.mutation<void, number>({
      invalidatesTags: [{
        id: 'LIST',
        type: 'Pages',
      }],
      query: (id) => ({
        method: 'DELETE',
        responseHandler: 'text',
        url: `/admin/v2/agg-pages/${id}`,
      }),
    }),
    deletePage: builder.mutation<null, number>({
      invalidatesTags: [{
        id: 'LIST',
        type: 'Pages',
      }],
      query: (id) => ({
        method: 'DELETE',
        url: `/admin/agg-pages/${id}`,
      }),
    }),
    getAggPagesV2: builder.query<GetAggPagesV2ResBody, GetAggPagesV2Params>({
      providesTags: [{
        id: 'LIST',
        type: 'Pages',
      }],
      query: ({
        slug,
        ...params
      }) => ({
        method: 'GET',
        params: {
          ...params,
          ...(slug && { search: slug }),
        },
        url: '/admin/v2/agg-pages',
      }),
    }),
    getAggPagesV2ById: builder.query<{ page: AggPageV2, products: Product[], region: string }, number>({
      providesTags: [{
        id: 'LIST',
        type: 'Pages',
      }],
      query: (id) => ({
        method: 'GET',
        url: `/admin/v2/agg-pages/${id}`,
      }),
    }),
    getFullPageData: builder.query<GetFullPageDataResBody, GetFullPageDataParams>({
      keepUnusedDataFor: 0,
      async queryFn({
        formMode,
        ...params
      }, { dispatch }) {
        try {
          const { endpoints: { getAggPagesV2ById } } = pagesApiSlice;

          const { endpoints: { getPublisherById } } = publishersApiSlice;

          const { endpoints: { getBrands } } = brandsApiSlice;

          const pageData: { page: AggPageV2; products: Product[]; region: string } =
            await dispatch(getAggPagesV2ById.initiate(params?.id as number, { forceRefetch: true }))
              .unwrap();

          let publisher;

          if (pageData?.page?.publisher_id) {
            const publisherResponse = await dispatch(getPublisherById.initiate(pageData?.page?.publisher_id))
              .unwrap();
            publisher = publisherResponse?.publisher;
          } else {
            publisher = {
              country: 'us',
              display_name: 'ProductPicks',
              domain: 'productpicks.ai',
              id: 0,
              name: 'ProductPicks',
              reverse_proxy_path: '',
            };
          }

          let brand = {};
          if (pageData?.page?.brand_id) {
            const brandResponse = await dispatch(getBrands.initiate({ ids: [pageData?.page?.brand_id] }))
              .unwrap();

            brand = {
              id: brandResponse?.brands[0]?.id,
              img_logo: brandResponse?.brands[0]?.img_logo,
              name: brandResponse?.brands[0]?.name,
              page_url: brandResponse?.brands[0]?.page_url,
            };
          }

          // const brand: Brand = {
          //   id: pageData?.brand?.id,
          //   img_logo: pageData?.brand?.img_logo,
          //   name: pageData?.brand?.name,
          //   page_url: pageData?.brand?.page_url,
          // };

          // console.log(brandResponse);

          const publisherImages = getPublisherImages(pageData.page.publisherImages);
          const pageBanners: PageBannerAsset[] = getBanners(pageData.page.assets);

          // const draftsRes = await dispatch(getPageDrafts.initiate(data.page.id)).unwrap();
          // const drafts: IDraft[] = getDrafts(draftsRes);

          const buyingGuide: PageBuyingGuide[] = [];
          if (pageData.page.buyingGuide) {
            buyingGuide.push({
              assets_type: 'buyingGuide',
              file_data: { text: pageData.page.buyingGuide.text },
              url: pageData.page.buyingGuide.img,
            });
          }

          const pagesProducts = [...pageData.products];

          const products: PageFormProduct[] = pagesProducts.reduce<PageFormProduct[]>(
            (result, product) => {
              const {
                assets,
                reviews,
              } = utilProductDataPreparing(product);
              const productAiImages: IProductAiImages['ids'][number] = {
                ai_image_url: product?.ai_images?.img_large || product?.ai_images?.img_medium,
                product_id: product.product_id,
                regenerate: false,
                title: product.title,
              };

              const mappedContent: PageFormProduct['content'] = {
                _id: `${product.asin}-origin`,
                bottomLine: product.content?.bottomLine || '',
                faq: product.content?.faq || [],
                product_id: String(product.asin),
                // @ts-ignore - v2 form uses { value: string }[] instead of string[]
                pros: (product.content?.pros || []).map((p: any) => typeof p === 'string' ? { value: p } : p),
                summary: product.content?.summary || '',
                tied: true,
                type: 'origin',
              };

              const product_id = String(product.product_marketplace === Marketplaces.Walmart ?
                product.w_item_id : product?.asin);

              result = [
                ...result,
                {

                  aiImages: productAiImages,
                  content: mappedContent,
                  original_id: product.id,
                  page_product_id: product.page_product_id,
                  imgLarge: product.img_large,
                  imgMedium: product.img_medium,
                  isHardcodedLink: product.is_hardcoded_link,
                  hardcodedLinkSource: product.hardcoded_link_source ?? null,
                  order: product.order ?? 0,
                  pageUrl: product.page_url,
                  pickedProductCopyId: product.picked_product_copy_id,
                  price: product.price,
                  product_id,
                  productAssets: assets,
                  reviews: {
                    product_id: String(product.asin),
                    reviews: Array.isArray(reviews.reviews) ? reviews.reviews : [],
                  },
                  product_marketplace: product.product_marketplace ?? Marketplaces.Amazon,
                  subtitle: product.subtitle,
                  title: product.title,
                  value: product.value,
                  linked_product_id: product.linked_product?.product_id || null,
                },
              ];

              return result;
            },
            [],
          );

          const author: AuthorDto = {
            created_at: '',
            first_name: pageData.page.author_first_name,
            gender: pageData.page.author_gender as 'male' | 'female',
            id: pageData.page.author_id,
            img: pageData.page.author_img,
            info: {
              about: '',
              city: '',
              country: '',
              experience: {
                specialization: '',
                years: 0,
              },
              state: '',
            },
            last_name: pageData.page.author_last_name,
            updated_at: '',
          };

          const response: GetFullPageDataResBody = {
            page: {
              author,
              brand_id: pageData.page.brand_id,
              brand,
              buyingGuide,
              created_at: pageData.page.created_at,
              domain_id: pageData.page.domain_id,
              id: pageData.page.id,
              pageBanners,
              products,
              publisher: publisher as Publisher,
              publisherImages,
              slug: pageData.page.slug,
              subtitle: pageData.page.subtitle,
              title: pageData.page.title?.trim() ?? '',
              updated_at: pageData.page.updated_at,
            },
          };
          return { data: response };
        } catch (e: unknown) {
          return {
            error: {
              error: e instanceof Error ? e.message : 'Unknown error occurred',
              status: 'CUSTOM_ERROR',
            } as FetchBaseQueryError,
          };
        }
      },
    }),
    getPageData: builder.query<GetPageDataResBody, GetPageDataParams>({
      query: ({
        domain,
        publisherName,
        ...params
      }) => ({
        headers: {
          'x-picks-domain': domain,
          'x-picks-publisher': publisherName,
        },
        params: {
          ...params,
          no_cache: 1,
        },
        url: '/pages/brands/attribution',
      }),
    }),
    getPageDrafts: builder.query<GetPageDraftsResBody, number>({
      query: (pageId) => ({
        method: 'GET',
        params: { no_cache: 1 },
        url: `/admin/agg-pages/drafts/${pageId}`,
      }),
    }),
    getPages: builder.query<GetPagesResBody, PagesFilterState>({
      providesTags: (result) => {
        if (Array.isArray(result.data.bestPages)) {
          return [
            ...result.data.bestPages.map(({ page_id }) => ({
              id: page_id,
              type: 'Pages',
            }) as const),
            {
              id: 'LIST',
              type: 'Pages',
            },
          ];
        }
        return [{
          id: 'LIST',
          type: 'Pages',
        }];
      },
      query: (params) => ({
        method: 'GET',
        params: { attribution: params.marketplace !== Marketplaces.Walmart, ...pickBy(params, identity()) },
        url: '/pages/best',
      }),
    }),
    deletePageAssets: builder.mutation<void, DeletePageAssetsParams>({
      query: ({
        pageId,
        ...body
      }) => ({
        body,
        method: 'DELETE',
        url: `/admin/agg-pages/assets/${pageId}`,
      }),
    }),
    deleteProductAssets: builder.mutation<void, DeleteProductAssetsParams>({
      query: (body) => ({
        body,
        method: 'DELETE',
        url: '/admin/agg-pages/products/assets',
      }),
    }),
    renewPageAssets: builder.mutation<void, RenewPageAssetsReqBody>({
      query: (body) => ({
        body,
        method: 'POST',
        url: '/admin/agg-pages/renew/assets',
      }),
    }),
    renewProductAssets: builder.mutation<void, UpdatedProductAsset>({
      query: (body) => ({
        body,
        method: 'POST',
        url: '/admin/agg-pages/products/renew/assets',
      }),
    }),
    submitProductAsset: builder.mutation<void, SubmitProductAssetReqBody>({
      query: ({
        assets_type,
        file,
        file_data,
        marketplace,
        marketplace_item_id,
        page_id,
      }) => {
        const formData = new FormData();
        formData.append('page_id', String(page_id));
        formData.append('file', file);
        formData.append('file_data', file_data);
        formData.append('assets_type', assets_type);
        formData.append('marketplace_item_id', String(marketplace_item_id));
        formData.append('marketplace', marketplace);
        return {
          body: formData,
          method: 'POST',
          url: '/admin/agg-pages/products/assets',
        };
      },
    }),
    submitAggPageProducts: builder.mutation<SubmitAggPageProductsResBody, SubmitAggPageProductsReqBody>({
      query: (body) => ({
        body,
        method: 'POST',
        url: '/admin/v2/agg-pages/products',
      }),
    }),
    submitPageAssets: builder.mutation<void, SubmitPageAssetsReqBody>({
      query: ({
        assets_type,
        file,
        file_data,
        page_id,
      }) => {
        const formData = new FormData();
        formData.append('page_id', String(page_id));
        formData.append('file', file);
        formData.append('file_data', file_data);
        formData.append('assets_type', assets_type);
        return {
          body: formData,
          method: 'POST',
          url: '/admin/agg-pages/assets',
        };
      },
    }),
    submitPageProcess: builder.mutation<{ pageId: number }, SubmitPageProcessData>({
      async queryFn({
        formData,
        formMode,
        formState: { dirtyFields },
        productsReordered,
      }, {
        dispatch,
        getState,
      }) {
        try {
          let createdPageId: number;

          // Read caches before any mutations run (mutations invalidate cache tags)
          const cachedPage = formData.id
            ? pagesApiSlice.endpoints.getAggPagesV2ById.select(formData.id)(
              getState() as Parameters<ReturnType<typeof pagesApiSlice.endpoints.getAggPagesV2ById.select>>[0],
            )
            : null;
          const cachedProducts = cachedPage?.data?.products ?? [];

          const {
            createAggPageV2,
            deletePageAssets,
            deleteProductAssets,
            renewPageAssets,
            renewProductAssets,
            submitAggPageProducts,
            submitPageAssets,
            submitProductAsset,
            updateAggPageV2,
          } = pagesApiSlice.endpoints;

          const {
            submitProductContent,
            updateProduct,
            updateProductReviews,
          } = productsApiSlice.endpoints;

          const { syncProductLinks } = productLinksApiSlice.endpoints;

          if (formMode === FormMode.CREATE) {
            const createPagePayload: CreateAggPageReqBody = {
              author_id: formData?.author?.id,
              slug: formData.slug,
              status: 'draft',
              subtitle: formData?.subtitle,
              title: formData.title,
              ...(formData?.publisher?.id ? { publisher_id: formData.publisher.id } : {}),
              ...(dirtyFields?.domain_id && { domain_id: formData?.domain_id }),
              ...(formData?.brand?.id ? { brand_id: formData.brand.id } : {}),
            };

            const createPageResponse = await dispatch(createAggPageV2.initiate(createPagePayload)) as
              { data: CreateAggPageResBody; error?: never } | { data?: never; error: FetchBaseQueryError };
            if ('error' in createPageResponse) {
              return { error: createPageResponse.error as FetchBaseQueryError };
            }
            createdPageId = createPageResponse.data?.page?.id;
          }

          if (formMode === FormMode.UPDATE) {
            const cachedAuthorId = cachedPage?.data?.page?.author_id;
            const updateBody: UpdateAggPageV2ReqBody = {
              ...(dirtyFields?.slug ? { slug: formData.slug } : {}),
              ...(dirtyFields?.title ? { title: formData.title } : {}),
              ...(formData.author?.id !== cachedAuthorId ? { author_id: formData.author?.id ?? null } : {}),
              ...(dirtyFields?.subtitle ? { subtitle: formData.subtitle } : {}),
            };

            if (Object.keys(updateBody).length) {
              const updateResponse = await dispatch(updateAggPageV2.initiate({
                pageId: formData?.id,
                body: updateBody,
              })) as { data: CreateAggPageResBody; error?: never } | { data?: never; error: FetchBaseQueryError };
              if ('error' in updateResponse) {
                return { error: updateResponse.error as FetchBaseQueryError };
              }
            }
          }

          const excludedItems: SubmitAggPageProductsReqBody['exclude_item_ids'] = formData.products
            .filter((product) => product.status === 'excluded')
            .map((excludedProduct) => excludedProduct.page_product_id);

          const visibleProducts = formData.products.filter((p) => p.status !== 'excluded');

          const hasNewProducts = visibleProducts.some((p) => p.status === 'new');
          const hasOrderChanges = productsReordered === true;

          if (excludedItems.length || hasNewProducts || hasOrderChanges) {
            const submitPagesProductsBody: SubmitAggPageProductsReqBody = {
              exclude_item_ids: excludedItems,
              page_id: formData?.id || createdPageId,
              products: visibleProducts.map((p) => ({
                label: p.value,
                product_id: p.original_id,
                product_marketplace: p.product_marketplace,
                sequence_order: p.order,
              })),
              region: formData?.publisher?.country || 'us',
            };

            const productsResponse = (await dispatch(
              submitAggPageProducts.initiate(submitPagesProductsBody),
            )) as
              | { data: SubmitAggPageProductsResBody; error?: never }
              | { data?: never; error: FetchBaseQueryError };
            if ('error' in productsResponse) {
              return { error: productsResponse.error as FetchBaseQueryError };
            }
          }

          const allFormProducts = formData.products;

          const hasLinkedProductChanges = allFormProducts.some((currentProduct) => {
            const cached = cachedProducts.find(
              (p) =>
                String(p.asin) === currentProduct.product_id ||
                String(p.w_item_id) === currentProduct.product_id,
            );

            const cachedLinkedId = cached?.linked_product?.product_id || null;
            const currentLinkedId = currentProduct.linked_product_id || null;

            return currentLinkedId !== cachedLinkedId;
          });

          if (hasLinkedProductChanges) {
            const submitLinksBody = {
              region: formData?.publisher?.country || 'us',
              links: allFormProducts.map((p) => ({
                product_id: p.original_id,
                product_marketplace: p.product_marketplace,
                linked_product_id: p.linked_product_id || null,
              })),
            };

            const linksResponse = (await dispatch(syncProductLinks.initiate(submitLinksBody))) as
              | { data: { success: boolean }; error?: never }
              | { data?: never; error: FetchBaseQueryError };

            if ('error' in linksResponse) {
              return { error: linksResponse.error as FetchBaseQueryError };
            }
          }

          // #3 Products
          //
          // const updatedOriginalProductContent: SubmitProductContentReqBody[] = [];
          // const updatedProductContent: UpdateProductContentCopyReqBody[] = [];
          // const newProductContent: CreateNewContentCopyReqBody[] = [];
          // const attachedProductContent: AttachContentProduct[] = [];
          // const updatedProductTitles: UpdateProductReqBody[] = [];
          //
          const updatedProductReviews: UpdateProductReviewsReqBody[] = [];
          const updatedProductContent: SubmitProductContentReqBody[] = [];
          const updatedProductTitles: { product_id: string; title: string; marketplace: string }[] = [];
          //
          // const deletedProductAssets: IAggPageDeleteProductAssets[] = [];
          // const addedProductAssets: IAggPageProductAssets[] = [];
          // const updatedProductAssets: UpdatedProductAsset[] = [];

          visibleProducts.forEach((currentProduct) => {
            // Product Titles
            // Title update: for new products compare against _initialTitle; for existing compare against cache
            const titleChanged = currentProduct.status === 'new'
              ? currentProduct.title !== currentProduct._initialTitle
              : formMode === FormMode.UPDATE && (() => {
              const cached = cachedProducts.find(
                (p) => String(p.asin) === currentProduct.product_id ||
                  String(p.w_item_id) === currentProduct.product_id,
              );
              return cached ? currentProduct.title !== cached.title : false;
            })();

            if (titleChanged && currentProduct.title) {
              updatedProductTitles.push({
                marketplace: currentProduct.product_marketplace,
                product_id: currentProduct.product_id,
                title: currentProduct.title,
              });
            }

            // Content
            /*
            const editedContentIdx = getExistedArrayElem(dirtyFields?.products[productIdx]?.content);

            editedContentIdx.forEach((contentIdx) => {
              const currentContent = currentProduct.content[contentIdx];

              const requestPayload: SubmitProductContentReqBody = {
                bottom_line: currentContent?.bottomLine,
                faq: currentContent?.faq || [],
                marketplace: formData.marketplace,
                marketplace_item_id: currentProduct.product_id,
                pros: currentContent?.pros?.filter((p) => p?.trim().length > 0) || [],
                summary: currentContent?.summary,
              };

              if (currentContent.type === 'new' && currentContent.tied) {
                attachedProductContent.push({ product_id: currentProduct.id });
              } else if (currentContent.type !== 'new'
                && currentContent.tied
                && currentProduct?.pickedProductCopyId !== currentContent.copy_id) {
                attachedProductContent.push({
                  product_copy_id: currentContent.copy_id,
                  product_id: currentProduct.id,
                });
              }

              if (currentContent.type === 'origin') {
                updatedOriginalProductContent.push(requestPayload);
              } else if (currentContent.type === 'new') {
                newProductContent.push({
                  ...requestPayload,
                  new_copy: true,
                });
              } else {
                updatedProductContent.push({
                  ...requestPayload,
                  item_copy_id: currentContent.copy_id,
                  new_copy: true,
                });
              }
            });
            */

            // Product Content — compare against cached original
            if (currentProduct.status !== 'new') {
              const originalProduct = cachedProducts.find(
                (p) => String(p.asin) === currentProduct.product_id ||
                  String(p.w_item_id) === currentProduct.product_id,
              );
              const originalContent = originalProduct?.content ?? null;
              const currentContent = currentProduct.content;
              const currentPros: string[] = (currentContent.pros ?? [])
                .map((p) => (typeof p === 'string' ? p : (p as { value: string }).value))
                .filter((p) => p?.trim().length > 0);

              const hasContentChanged = originalContent
                ? JSON.stringify({
                badge: currentContent.badge,
                bottomLine: currentContent.bottomLine,
                faq: currentContent.faq,
                pros: currentPros,
                summary: currentContent.summary,
              }) !== JSON.stringify({
                badge: originalContent.badge,
                bottomLine: originalContent.bottomLine,
                faq: originalContent.faq,
                pros: originalContent.pros ?? [],
                summary: originalContent.summary,
              })
                : false;

              if (currentContent && hasContentChanged) {
                updatedProductContent.push({
                  badge: currentContent.badge,
                  bottom_line: currentContent.bottomLine,
                  faq: currentContent.faq || [],
                  marketplace: currentProduct.product_marketplace,
                  marketplace_item_id: currentProduct.product_id,
                  pros: currentPros,
                  summary: currentContent.summary,
                });
              }
            }

            // Product Reviews — skip new products; compare current reviews against store cache
            if (currentProduct.status === 'new') return;
            const originalProduct = cachedProducts.find(
              (p) => String(p.asin) === currentProduct.product_id ||
                String(p.w_item_id) === currentProduct.product_id,
            );
            if (originalProduct) {
              const { reviews: { reviews: originalReviews } } = utilProductDataPreparing(originalProduct);
              const currentReviews = currentProduct.reviews?.reviews ?? [];
              if (JSON.stringify(currentReviews) !== JSON.stringify(originalReviews)) {
                updatedProductReviews.push({
                  marketplace: currentProduct.product_marketplace,
                  marketplace_item_id: currentProduct.product_id,
                  reviews: currentReviews,
                });
              }
            }

            //   // Product Assets
            //   const editedAssetsIdx = getExistedArrayElem(dirtyFields?.products[productIdx]?.productAssets);
            //
            //   const updatedAssets: UpdatedAsset[] = [];
            //
            //   editedAssetsIdx.forEach((assetIdx) => {
            //     const currentAsset = currentProduct.productAssets[assetIdx];
            //     const order = assetIdx - currentProduct.productAssets.filter((elem) => {
            //       if (isVideoAsset(currentAsset) || isPostAsset(elem)) {
            //         return elem.assets_type === currentAsset.assets_type;
            //       }
            //       if (isBannerAsset(elem) || isCardAsset(elem)) {
            //         return currentAsset.assets_type === 'assets' &&
            //         elem.file_data.type === currentAsset.file_data.type;
            //       }
            //
            //       return false;
            //     })
            //       .reduce<number>((result, elem, index) => {
            //         if (elem.type === 'deleted' && index < assetIdx) {
            //           result += 1;
            //           return result;
            //         }
            //         return result;
            //       }, 0);
            //
            //     if (currentAsset.type === 'deleted') {
            //       deletedProductAssets.push({
            //         assets_type: currentAsset?.assets_type,
            //         link: currentAsset.url,
            //         marketplace: formData.marketplace,
            //         marketplace_item_id: currentProduct.product_id,
            //       });
            //     } else if (currentAsset.type === 'added') {
            //       addedProductAssets.push({
            //         assets_type: currentAsset?.assets_type,
            //         file: currentAsset?.file,
            //         file_data: JSON.stringify({
            //           ...currentAsset?.file_data,
            //           order,
            //         }),
            //         marketplace: formData.marketplace,
            //         marketplace_item_id: currentAsset.product_id,
            //       });
            //     } else {
            //       updatedAssets.push({
            //         asset_type: currentAsset.assets_type,
            //         data: JSON.stringify({
            //           ...currentAsset
            //             .file_data,
            //           order,
            //         }),
            //         link: currentAsset.url,
            //       });
            //     }
            //   });
            //
            //   (['assets', 'video', 'blogs'] as const).forEach((assetType) => {
            //     const filteredAssets = updatedAssets.filter((asset) => asset.asset_type === assetType);
            //
            //     if (filteredAssets.length) {
            //       updatedProductAssets.push({
            //         assets_type: assetType,
            //         marketplace: formData.marketplace,
            //         marketplace_item_id: currentProduct.product_id,
            //         product_assets: filteredAssets.map((elem) => ({
            //           data: elem.data,
            //           link: elem.link,
            //         })),
            //       });
            //     }
            //   });
            //
            //   console.log({
            //     editedAssetsIdx,
            //     editedReviewsIdx,
            //     productId: currentProduct.product_id,
            //   });
            // });
            //
            // console.log('deletedProductAssets', deletedProductAssets);
            // console.log('addedProductAssets', addedProductAssets);
            // console.log('updatedProductAssets', updatedProductAssets);
            //
            // // :TODO Check out if this is possible to merge into one next 3 requests
            // // Origin product content updating
            // if (updatedOriginalProductContent.length) {
            //   // await Promise.all(
            //   //   updatedOriginalProductContent.map((elem) => dispatch(submitProductContent.initiate(elem))
            //   //     .unwrap()),
            //   // );
            // }

            // // Product Content Copies updating
            // if (updatedProductContent.length) {
            //   // await Promise.all(
            //   //   updatedProductContent.map((elem) => dispatch(submitProductContent.initiate(elem))
            //   //     .unwrap()),
            //   // );
            // }
            //
            // // Submit New Product Content Copies
            //
            // const createdContentRes: SubmitProductContentResBody[] = [];
            // if (newProductContent.length) {
            //   // createdContentRes = await Promise.all(
            //   //   newProductContent.map((elem) => dispatch(submitProductContent.initiate(elem))
            //   //     .unwrap()),
            //   // );
            // }

            // // Attach product content
            //
            // const attachedProducts = attachedProductContent.reduce<AttachContentProduct[]>((result, elem) => {
            //   if (elem?.product_id && elem?.product_copy_id) {
            //     result = [...result, elem];
            //   } else if (elem?.product_id && !elem?.product_copy_id) {
            //     const { product_copy_id } = createdContentRes.find((item) => item.product.id === elem?.product_id);
            //
            //     result = [...result, {
            //       product_copy_id,
            //       product_id: elem?.product_id,
            //     }];
            //   }
            //   return result;
            // }, []);
            //
            // // Update Product Titles
            // if (updatedProductTitles.length) {
            //   // await Promise.all(updatedProductTitles.map((elem) => dispatch(updateProduct.initiate(elem))));
            // }

            // // Submit Pages Products
            // const isProductsForUpdateExist = dirtyFields.products
            //   .some((product) => product?.order || product?.value || product?.status);
            // const excludedProducts = formData.products.filter((elem) => elem.status === 'excluded') || [];
            //
            // const submitPagesProductsPayload: SubmitPagesProductsReqBody = {
            //   keyword_id: formData.keywordId,
            //   marketplace: formData.marketplace,
            //   ...(isProductsForUpdateExist && {
            //     products: formData.products.map((elem, index) => ({
            //       marketplace_item_id: elem.product_id,
            //       order: index + 1,
            //       value: elem.value,
            //     })),
            //   }),
            //   exclude_marketplace_item_ids: excludedProducts.map((elem) => elem.product_id),
            // };
            //
            // if (isProductsForUpdateExist || excludedProducts?.length) {
            //   // await dispatch(submitPagesProducts.initiate(submitPagesProductsPayload));
            // }
          });

          // Update Product Titles
          if (updatedProductTitles.length) {
            await Promise.all(
              updatedProductTitles.map(({
                  marketplace,
                  product_id,
                  title,
                }) =>
                  dispatch(updateProduct.initiate({
                    data: {
                      marketplace: marketplace as Marketplaces,
                      payload: { title },
                    },
                    product_id,
                  }))
                    .unwrap(),
              ),
            );
          }

          // Update Reviews — after loop so all products are collected first
          if (updatedProductReviews.length) {
            await Promise.all(
              updatedProductReviews.map((elem) => dispatch(updateProductReviews.initiate(elem))
                .unwrap()),
            );
          }

          // Update Product Content
          if (updatedProductContent.length) {
            await Promise.all(
              updatedProductContent.map((elem) => dispatch(submitProductContent.initiate(elem))
                .unwrap()),
            );
          }

          const _pageId = (formData?.id || createdPageId) as number;
          const toUpload: SubmitPageAssetsReqBody[] = [];
          const toDelete: DeletePageAssetsParams[] = [];
          const toRenew: RenewPageAssetsReqBody[] = [];
          const bannerRenewAssets: { img?: string; data?: string }[] = [];
          const pubImgRenewAssets: { img?: string; data?: string }[] = [];

          if (dirtyFields?.buyingGuide) {
            formData.buyingGuide?.forEach((elem) => {
              if (elem.type === 'new') {
                toUpload.push({
                  assets_type: elem.assets_type,
                  file: elem.file,
                  file_data: JSON.stringify(elem.file_data),
                  page_id: _pageId,
                });
              } else if (
                elem.type === 'deleted' &&
                !formData.buyingGuide.some((a) => a.type === 'new')
              ) {
                toDelete.push({
                  assets_type: elem.assets_type,
                  link: elem.url,
                  pageId: _pageId,
                });
              } else if (elem.type === 'renew') {
                toRenew.push({
                  assets_type: elem.assets_type,
                  page_assets: [pickBy({
                    data: JSON.stringify(elem.file_data),
                    img: elem.url,
                  }, identity)],
                  page_id: _pageId,
                });
              }
            });
          }

          if (formData.pageBanners?.some((b) => b.type === 'new' || b.type === 'deleted' || b.type === 'renew')) {
            let bannerOrder = 1;
            formData.pageBanners?.forEach((elem) => {
              if (elem.type === 'deleted') {
                toDelete.push({
                  assets_type: elem.assets_type,
                  link: elem.url,
                  pageId: _pageId,
                });
                return;
              }
              elem.file_data.order = bannerOrder++;
              if (elem.type === 'new') {
                toUpload.push({
                  assets_type: elem.assets_type,
                  file: elem.file,
                  file_data: JSON.stringify(elem.file_data),
                  page_id: _pageId,
                });
              } else {
                bannerRenewAssets.push({
                  data: JSON.stringify(elem.file_data),
                  img: elem.url,
                });
              }
            });
          }

          if (formData.publisherImages?.some((img) =>
            img.type === 'new' || img.type === 'deleted' || img.type === 'renew')) {
            let pubImgOrder = 1;
            formData.publisherImages?.forEach((elem) => {
              if (elem.type === 'deleted') {
                toDelete.push({
                  assets_type: elem.assets_type,
                  link: elem.url,
                  pageId: _pageId,
                });
                return;
              }
              elem.file_data = {
                ...elem.file_data,
                order: pubImgOrder++,
              };
              if (elem.type === 'new') {
                toUpload.push({
                  assets_type: elem.assets_type,
                  file: elem.file,
                  file_data: JSON.stringify(elem.file_data),
                  page_id: _pageId,
                });
              } else {
                pubImgRenewAssets.push({
                  data: JSON.stringify(elem.file_data),
                  img: elem.url,
                });
              }
            });
          }

          toRenew.push(
            {
              assets_type: 'assets',
              page_assets: bannerRenewAssets,
              page_id: _pageId,
            },
            {
              assets_type: 'publisherImages',
              page_assets: pubImgRenewAssets,
              page_id: _pageId,
            },
          );

          // Product video assets
          const toRenewProductAssets: UpdatedProductAsset[] = [];
          const toUploadProductAssets: SubmitProductAssetReqBody[] = [];
          const toDeleteProductAssets: DeleteProductAssetsParams[] = [];
          formData.products.forEach((product) => {
            if (product.status === 'excluded') return;

            const isVideoAsset = (a: PageFormProduct['productAssets'][number]): a is ProductVideoAsset =>
              a.assets_type === 'video';

            const cachedOriginalProduct = cachedProducts.find(
              (p) => String(p.asin) === product.product_id || String(p.w_item_id) === product.product_id,
            );
            const originalVideos = cachedOriginalProduct
              ? utilProductDataPreparing(cachedOriginalProduct)
                .assets
                .filter((a) => a.assets_type === 'video')
              : [];

            const currentVideos = product.productAssets?.filter(isVideoAsset) ?? [];
            const visibleVideos = currentVideos.filter((a) => a.type !== 'deleted');

            const hasVideoChanges = currentVideos.some((a) => a.type === 'added' || a.type === 'deleted') ||
              (!!cachedOriginalProduct && visibleVideos.some((a, idx) => {
                if (a.type === 'added') return false;
                const original = originalVideos.find((o) => o.url === a.url);
                return !original ||
                  original.file_data.text !== a.file_data?.text ||
                  original.file_data.order !== idx + 1;
              }));

            if (hasVideoChanges) {
              const videoRenewAssets: ProductAsset[] = [];
              let videoOrder = 0;

              product.productAssets?.forEach((asset) => {
                if (!isVideoAsset(asset)) return;

                if (asset.type === 'deleted') {
                  toDeleteProductAssets.push({
                    assets_type: 'video',
                    link: asset.url,
                    marketplace: product.product_marketplace,
                    marketplace_item_id: product.product_id,
                  });
                  return;
                }

                videoOrder++;

                if (asset.type === 'added') {
                  toUploadProductAssets.push({
                    assets_type: 'video',
                    file: asset.file,
                    file_data: JSON.stringify({
                      text: asset.file_data?.text || '',
                      order: videoOrder,
                    }),
                    marketplace: product.product_marketplace,
                    marketplace_item_id: product.product_id,
                    page_id: _pageId,
                  });
                } else {
                  const original = originalVideos.find((o) => o.url === asset.url);
                  const textChanged = !original || original.file_data.text !== (asset.file_data?.text || '');
                  const orderChanged = !original || original.file_data.order !== videoOrder;
                  if (textChanged || orderChanged) {
                    videoRenewAssets.push({
                      link: asset.url,
                      data: JSON.stringify({
                        product_id: product.product_id,
                        text: asset.file_data?.text || '',
                        order: videoOrder,
                      }),
                    });
                  }
                }
              });

              if (videoRenewAssets.length) {
                toRenewProductAssets.push({
                  assets_type: 'video',
                  marketplace: product.product_marketplace,
                  marketplace_item_id: product.product_id,
                  product_assets: videoRenewAssets,
                });
              }
            }

            // Banner, card, post assets
            const isBannerOrCard = (a: PageFormProduct['productAssets'][number]):
              a is ProductBannerAsset | ProductCardAsset => a.assets_type === 'assets';
            const isPost = (a: PageFormProduct['productAssets'][number]): a is ProductPostAsset =>
              a.assets_type === 'blogs';

            const originalCachedAssets = cachedOriginalProduct
              ? utilProductDataPreparing(cachedOriginalProduct).assets
              : [];

            ([
              {
                assetsType: 'assets' as const,
                filter: isBannerOrCard,
              },
              {
                assetsType: 'blogs' as const,
                filter: isPost,
              },
            ] as const).forEach(({
              assetsType,
              filter,
            }) => {
              const currentGroup = product.productAssets?.filter(filter) ?? [];
              const originalGroup = originalCachedAssets.filter((a) => a.assets_type === assetsType);
              const visibleGroup = currentGroup.filter((a) => a.type !== 'deleted');

              const hasChanges = currentGroup.some((a) => a.type === 'added' || a.type === 'deleted') ||
                (!!cachedOriginalProduct && visibleGroup.some((a, idx) => {
                  if (a.type === 'added') return false;
                  const orig = originalGroup.find((o) => o.url === a.url);
                  if (!orig) return true;
                  if (orig.file_data.order !== undefined && orig.file_data.order !== idx + 1) return true;
                  if ((orig.file_data.text || '') !== ((a.file_data as any)?.text || '')) return true;
                  const aFileData = a.file_data as any;
                  const origFileData = orig.file_data as any;
                  if ('title' in aFileData && (origFileData.title || '') !== (aFileData.title || '')) return true;
                  return false;
                }));

              if (!hasChanges) return;

              const renewAssets: ProductAsset[] = [];
              let order = 0;

              currentGroup.forEach((asset) => {
                if (asset.type === 'deleted') {
                  toDeleteProductAssets.push({
                    assets_type: assetsType,
                    link: asset.url,
                    marketplace: product.product_marketplace,
                    marketplace_item_id: product.product_id,
                  });
                  return;
                }

                order++;

                if (asset.type === 'added') {
                  toUploadProductAssets.push({
                    assets_type: assetsType,
                    file: asset.file,
                    file_data: JSON.stringify({
                      ...(asset as any).file_data,
                      order,
                    }),
                    marketplace: product.product_marketplace,
                    marketplace_item_id: product.product_id,
                    page_id: _pageId,
                  });
                } else {
                  const orig = originalGroup.find((o) => o.url === asset.url);
                  const fileData = (asset as any).file_data ?? {};
                  const textChanged = !orig || (orig.file_data.text || '') !== (fileData.text || '');
                  const origTitle = (orig?.file_data as any)?.title as string | undefined;
                  const titleChanged = 'title' in fileData && (origTitle || '') !== (fileData.title || '');
                  const orderChanged = orig?.file_data.order !== undefined && orig.file_data.order !== order;
                  if (textChanged || titleChanged || orderChanged) {
                    renewAssets.push({
                      link: asset.url,
                      data: JSON.stringify({
                        ...fileData,
                        order,
                      }),
                    });
                  }
                }
              });

              if (renewAssets.length) {
                toRenewProductAssets.push({
                  assets_type: assetsType,
                  marketplace: product.product_marketplace,
                  marketplace_item_id: product.product_id,
                  product_assets: renewAssets,
                });
              }
            });
          });

          let assetsChain: Promise<unknown> = Promise.resolve();

          toRenew
            .filter((p) => p.page_assets?.length)
            .forEach((payload) => {
              assetsChain = assetsChain.then(() => dispatch(renewPageAssets.initiate(payload))
                .unwrap());
            });

          toUpload.forEach((payload) => {
            assetsChain = assetsChain.then(() => dispatch(submitPageAssets.initiate(payload))
              .unwrap());
          });

          toDelete.forEach((payload) => {
            assetsChain = assetsChain.then(() => dispatch(deletePageAssets.initiate(payload))
              .unwrap());
          });

          toRenewProductAssets.forEach((payload) => {
            assetsChain = assetsChain.then(() => dispatch(renewProductAssets.initiate(payload))
              .unwrap());
          });

          toUploadProductAssets.forEach((payload) => {
            assetsChain = assetsChain.then(() => dispatch(submitProductAsset.initiate(payload))
              .unwrap());
          });

          toDeleteProductAssets.forEach((payload) => {
            assetsChain = assetsChain.then(() => dispatch(deleteProductAssets.initiate(payload))
              .unwrap());
          });

          await assetsChain;

          return { data: { pageId: formData?.id || createdPageId } };
        } catch
          (e: any) {
          // eslint-disable-next-line no-console
          console.error(e);
          return { error: e };
        }
      }
      ,
    }),
    submitPagesProducts: builder.mutation<null, SubmitPagesProductsReqBody>({
      query: (body) => ({
        body,
        method: 'POST',
        url: '/admin/agg-pages/products',
      }),
    }),
    updateAggPageV2: builder.mutation<CreateAggPageResBody, UpdateAggPageV2Params>({
      invalidatesTags: [{
        id: 'LIST',
        type: 'Pages',
      }],
      query: ({
        body,
        pageId,
      }) => ({
        body,
        method: 'PUT',
        url: `/admin/v2/agg-pages/${pageId}`,
      }),
    }),
    updatePageData: builder.query<UpdatePageDataResBody, UpdatePageDataReqBody>({
      query: ({
        pageId,
        payload,
      }) => ({
        body: payload,
        method: 'PUT',
        url: `/admin/agg-pages/${pageId}`,
      }),
    }),
    updatePageStatus: builder.mutation<IUpdatePageResponse, UpdatePageStatusReqBody>({
      invalidatesTags: [{
        id: 'LIST',
        type: 'Pages',
      }],
      query: ({
        pageId,
        payload,
      }) => ({
        body: pickBy(payload, identity()),
        method: 'PUT',
        url: `/admin/agg-pages/${pageId}`,
      }),
    }),
    validateAggPageSlugV2: builder.mutation<ValidateAggPageSlugResBody, ValidateAggPageSlugReqBody>({
      query: (body) => ({
        body,
        method: 'POST',
        url: '/admin/v2/agg-pages/validate',
      }),
    }),
    validateKeyword: builder.mutation<ValidateKeywordResBody, ValidateKeywordReqBody>({
      query: (body) => ({
        body,
        method: 'POST',
        url: '/admin/agg-pages/validate/keyword',
      }),
    }),
  }),
  reducerPath: 'pagesApi',
  tagTypes: ['Pages'],
});
